/**
 * Model Store
 *
 * Currently only stores model (including LoRAs) versions
 */

import { skipToken } from '@apollo/client/react'
import { produce } from 'immer'
import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import {
  createElement,
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type {
  GenerationModelDetailFragment,
  GenerationModelVersion,
  GetGenerationModelByVersionIdQuery,
} from 'src/services/apollo/generated/graphql'
import {
  fetchGetGenerationModelByVersionId,
  useGetGenerationModel,
} from 'src/services/apollo/generation-model'
import {
  useDynamicConstants,
  useDynamicGenerationModelConfig,
} from 'src/services/constants'

const store = getDefaultStore()

export type ModelVersion =
  GetGenerationModelByVersionIdQuery['generationModelVersion']

/**
 * TODO: Revisit this
 * Is the custom cache still necessary with Apollo's built-in cache?
 */
export const modelCacheAtom = atom(
  {} as Record<string, undefined | { value: ModelVersion; updatedAt: number }>,
)

const THROTTLE = 6e5 // 10 minutes
const EMPTY_MODEL_VERSION_RECORD = Object.freeze({}) as Record<
  string,
  ModelVersion | undefined
>

/**
 * Get a model version info
 *
 * Always returns the cached data, but fires a sync automatically. Recommended
 * to use the hook `useModelVersion` instead, unless you really intend to access
 * values from cache directly.
 */
export async function getModelVersion(versionId: string | undefined) {
  if (!versionId) return undefined

  const cached = store.get(modelCacheAtom)[versionId]
  if (!cached || Date.now() - cached.updatedAt >= THROTTLE) {
    const res = await syncModelVersion(versionId)
    if (res) {
      return res
    }
  }
  return cached?.value
}

/**
 * Save a mode version info to cache
 */
export function setModelVersion(versionId: string, model: ModelVersion) {
  store.set(
    modelCacheAtom,
    produce(cache => {
      cache[versionId] = {
        value: model,
        updatedAt: Date.now(),
      }
    }),
  )
}

const fetchers = new Map<string, Promise<GenerationModelVersion | undefined>>()

async function fetchModelVersion(
  id: string,
): Promise<GenerationModelVersion | undefined> {
  if (fetchers.has(id)) {
    return fetchers.get(id)
  } else {
    const fetcher = fetchGetGenerationModelByVersionId({ id })
      .then(res => {
        const version = res.data?.generationModelVersion
        setModelVersion(id, version)
        return version
      })
      .finally(() => {
        fetchers.delete(id)
      })
    fetchers.set(id, fetcher)
    return fetcher
  }
}

/**
 * Sync a model version info from server
 */
async function syncModelVersion(
  versionId: string,
): Promise<GenerationModelVersion | undefined> {
  return await fetchModelVersion(versionId)
}

type UseModelVersionReturn<T> = T extends string[]
  ? Record<T[number], ModelVersion | undefined>
  : T extends string
    ? ModelVersion | undefined
    : undefined

export function useModelVersion<T extends undefined | string | string[]>(
  versionId: T,
): UseModelVersionReturn<T> {
  const depKey = Array.isArray(versionId)
    ? JSON.stringify(versionId)
    : versionId

  // Activate the jotai effect to track state changes.
  // Only the requested versions are needed to track.
  // So select them for better performance.
  const cache = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          modelCacheAtom,
          cache => {
            if (!versionId) return undefined
            if (typeof versionId === 'string') return cache[versionId]
            // Otherwise, it's an array
            return versionId.map(id => cache[id])
          },
          // Extracts concatenated `updatedAt` for equality check
          (a, b) =>
            (Array.isArray(a)
              ? a
                  .map(it => it?.updatedAt)
                  .sort()
                  .join()
              : a?.updatedAt) ===
            (Array.isArray(b)
              ? b
                  .map(it => it?.updatedAt)
                  .sort()
                  .join()
              : b?.updatedAt),
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [depKey],
    ),
  )

  const [result, setResult] = useState<UseModelVersionReturn<T> | undefined>(
    undefined,
  )

  useEffect(() => {
    let canceled = false
    // Always reset when versionId changes
    setResult(undefined)

    if (!versionId) {
      return () => {
        canceled = true
      }
    }

    if (typeof versionId === 'string') {
      getModelVersion(versionId)
        .then(res => {
          if (!canceled) {
            setResult(res as UseModelVersionReturn<T>)
          }
        })
        .catch(error => {
          // Prevent unhandled promise rejection and reset result on failure
          console.error('Failed to load model version', error)
          setResult(undefined)
        })
    } else {
      Promise.all(versionId.map(id => getModelVersion(id)))
        .then(results => {
          setResult(
            Object.fromEntries(
              versionId.map((id, i) => [id, results[i]]),
            ) as UseModelVersionReturn<T>,
          )
        })
        .catch(error => {
          // Prevent unhandled promise rejection and reset result on failure
          console.error('Failed to load one or more model versions', error)
          setResult(undefined)
        })
    }

    return () => {
      canceled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey, cache])

  // Return undefined for missing/string ids, empty record fallback only for array inputs
  if (!versionId) {
    return undefined as UseModelVersionReturn<T>
  }
  return typeof versionId === 'string'
    ? (result as UseModelVersionReturn<T>)
    : ((result ?? EMPTY_MODEL_VERSION_RECORD) as UseModelVersionReturn<T>)
}

/*
 * =============================================================================
 */

export function UseGenerationModel(props: {
  modelId?: string
  children?: (props: { model?: GenerationModelDetailFragment }) => ReactNode
}) {
  const { data } = useGetGenerationModel(props.modelId || skipToken)

  return createElement(Fragment, null, props.children?.({ model: data }))
}

/*
 * =============================================================================
 */

/**
 * Hooks to get base model ids for model market filter
 */
export function useBaseModelIdsForMarkFilter() {
  const { data } = useDynamicConstants()
  const baseModels = data?.baseModelsForModelMarketFilter ?? {}
  return Object.entries(baseModels).map(([value, label]) => ({
    value,
    label,
  }))
}

/**
 * Hooks to get LoRA categories
 */
export function useLoraCategories() {
  const { data } = useDynamicGenerationModelConfig()
  const loraCategories = data?.loraCategories ?? {}
  return Object.entries(loraCategories).map(([value, label]) => ({
    value,
    label,
  }))
}
